import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { validateGameId } from '@intelligence/v3/utils/topcharts';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CardSlotV2 from '@/pages/intelligence/components/CardSlotV2';
import StoreRanksTopRankGame from '../StoreRanksTopRankGame';
import StoreRanksNumtrend from '@/pages/intelligence/pages/StoreRanks/components/StoreRanksNumtrend';
import ARightClickToJump from '@/pages/intelligence/components/ARightClickToJump';
import { ifCanChangeNumberFun } from '@intelligence/utils/commonUtils';
import './index.scss';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { IStoreRankTopRank } from '@/pages/intelligence/types/storeRank';

interface IStoreRanksTopCommcardProps extends IBaseProps {
  // 榜单游戏类型
  entityType: 'mobile' | 'pc' | 'console' | 'minigame';
  gameIdKey: string;
  // 榜单title
  title: string;
  // 榜单title下方的文案
  subTitle?: string;
  source: string;
  // 榜单的最新更新时间
  lastDate: string;
  list: IStoreRankTopRank[];
  // 是否展示游戏平台icons
  isShowPlatformIcon?: boolean;
  // 是否展示DataBrain的icon
  isShowDataBrainIcon?: boolean;
  // 榜单的无游戏rank数据UI renderer
  noDataRenderer?: (payload: { source: string; lastDate: string }) => React.ReactElement;
  // 榜单title的click回调
  onTitleClick: (e: React.SyntheticEvent<HTMLDivElement>) => void;
  // 点击icon、name的回调
  onClick?: (payload: IStoreRankTopRank, isValidGameId: boolean, e: React.SyntheticEvent<HTMLElement>) => void;
  getDetailGamePath?: (payload: IStoreRankTopRank, isValidGameId: boolean) => string;
}

const StoreRanksTopCommcard: React.FC<IStoreRanksTopCommcardProps> = (props) => {
  const {
    isShowPlatformIcon,
    isShowDataBrainIcon,
    isShowMarketIcon = true,
    entityType,
    gameIdKey,
    title,
    subTitle,
    source,
    lastDate,
    list,
    noDataRenderer: noDataRendererOuter,
    onTitleClick,
    onClick,
    getDetailGamePath,
  } = props;
  const { formatMessage } = useIntl();

  const isNoData = !list?.length;

  const defaultNoDataRenderer = () => (
    <div className="dbi-storeranks-topcommcard-nodata">
      {formatMessage({ id: 'dbi_store_rank_no_data_under_current_selections' })}
      {lastDate && (
        <>
          <br />
          {formatMessage({ id: 'dbi_store_rank_last_updated_on' }, { date: lastDate })}
        </>
      )}
    </div>
  );

  const noDataRenderer = typeof noDataRendererOuter === 'function' ? noDataRendererOuter : defaultNoDataRenderer;

  const getGameNameTagValue = (topItem: any) => {
    if (topItem.consecutive_days > 0) {
      return {
        text: formatMessage({ id: 'dbi_wechat_top_for_day' }, { dayNum: topItem.consecutive_days }),
        type: 'yellow',
      };
    }
    if (topItem.rank_change && !ifCanChangeNumberFun(topItem.rank_change)) {
      if (topItem.rank_change === '新进榜' || topItem.rank_change === 'New Entry') {
        return {
          text: formatMessage({ id: 'dbi_wechat_new_entry' }),
          type: 'blue',
        };
      }
      return null;
    }
    return null;
  };

  return (
    <CardSlotV2
      className="dbi-storeranks-topcommcard"
      title={
        <div>
          <div className="dbi-storeranks-topcommcard-head" onClick={onTitleClick}>
            <div className="dbi-storeranks-topcommcard-title">
              <span>{title}</span>
              <SvgIcon className="dbi-storeranks-topcommcard-icon" type="arrow-right" />
            </div>
          </div>
          {subTitle && <div className="dbi-storeranks-topcommcard-text">{subTitle}</div>}
        </div>
      }
      iconList={[]}
    >
      {/* 榜单Rank 无数据UI */}
      {isNoData && noDataRenderer({ source, lastDate })}
      {/* 榜单Rank */}
      {isNoData ||
        list?.map((item) => {
          const { source_metric_list: sourceMetrics } = item ?? {};
          // @ts-ignore
          const gameId = item?.[gameIdKey] || '';
          const isValidGameId = validateGameId(gameId);
          const rankMetric = sourceMetrics?.[0] ?? {};

          return (
            <div key={gameId} className={classnames('dbi-storeranks-topcommcard-cont', { 'is-valid-game-id': isValidGameId })}>
              <div className="dbi-storeranks-topcommcard-info">
                <ARightClickToJump href={getDetailGamePath?.(item, isValidGameId)}/* style={{ display: 'inline' }}*/>

                  <StoreRanksTopRankGame
                    entityType={entityType}
                    name={item?.name}
                    cover={item?.cover}
                    rank={item?.rank}
                    market={item?.market}
                    publisher={item?.publisher}
                    platforms={item?.platform}
                    isShowPlatformIcon={isShowPlatformIcon}
                    isShowDataBrainIcon={isShowDataBrainIcon}
                    isShowMarketIcon={isShowMarketIcon}
                    gameNameTag={getGameNameTagValue(item)}
                    onClick={(e) => {
                      onClick?.(item, isValidGameId, e);
                    }}
                  />
                </ARightClickToJump>
              </div>
              <StoreRanksNumtrend num={item?.rankChange || rankMetric?.value} />
            </div>
          );
        })}
    </CardSlotV2>
  );
};

export default StoreRanksTopCommcard;
